/**
 * LONGEVX · utilidades globales
 * - Revelado suave al hacer scroll (.lx-reveal)
 * - Agregar al carrito desde secciones propias ([data-lx-add]) y abrir el carrito lateral de Horizon
 */
import { CartLinesUpdateEvent } from '@shopify/events';

/* ---------- Reveal ---------- */
(() => {
  const els = document.querySelectorAll('.lx-reveal');
  if (!els.length) return;
  if (!('IntersectionObserver' in window)) {
    els.forEach((el) => el.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  els.forEach((el) => io.observe(el));
})();

/* ---------- Carrito ---------- */
async function fetchCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems && typeof cartItems.fetchCartData === 'function') {
    await customElements.whenDefined('cart-items-component');
    return cartItems.fetchCartData();
  }
  const res = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return res.json();
}

/**
 * @param {HTMLElement} trigger
 * @param {string|number} variantId
 * @param {number} quantity
 */
export async function lxAddToCart(trigger, variantId, quantity = 1) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);

  const body = new FormData();
  body.append('id', String(variantId));
  body.append('quantity', String(quantity));
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  let deferred = null;
  try {
    deferred = CartLinesUpdateEvent.createPromise();
    trigger.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: deferred.promise,
      })
    );
  } catch (e) {
    deferred = null;
  }

  try {
    const res = await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add'}.js`, {
      method: 'POST',
      body,
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      credentials: 'same-origin',
    });
    const json = await res.json();
    if (json.status) throw new Error(json.description || json.message || 'Error');

    const cart = await fetchCart();
    if (deferred) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'lx-add',
          sourceId: trigger.id || 'lx',
          itemCount: quantity,
          sections: json.sections,
          didError: false,
        },
      });
    }

    // Si no existe carrito lateral, ir al carrito.
    if (!document.querySelector('cart-drawer-component')) {
      window.location.href = window.Theme?.routes?.cart_url || '/cart';
    }
    return true;
  } catch (err) {
    if (deferred) deferred.reject(err);
    console.warn('[LONGEVX] No se pudo agregar al carrito', err);
    window.location.href = `/cart/add?id=${variantId}&quantity=${quantity}`;
    return false;
  }
}

document.addEventListener('click', async (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-lx-add]') : null;
  if (!btn) return;
  event.preventDefault();
  const id = btn.getAttribute('data-variant-id');
  if (!id) return;
  const qtyInput = btn.closest('[data-lx-buy]')?.querySelector('[data-lx-qty]');
  const qty = Math.max(1, parseInt(qtyInput?.value || '1', 10));
  btn.setAttribute('aria-busy', 'true');
  btn.classList.add('is-loading');
  const label = btn.querySelector('[data-label]');
  const original = label?.textContent;
  const ok = await lxAddToCart(btn, id, qty);
  btn.classList.remove('is-loading');
  btn.removeAttribute('aria-busy');
  if (ok && label) {
    label.textContent = btn.getAttribute('data-added') || 'Agregado';
    setTimeout(() => (label.textContent = original), 2200);
  }
});

/* Enlaces externos (redes, firma, referencias): siempre en una ventana nueva */
const lxExternal = (root = document) => {
  root.querySelectorAll('a[href^="http"]').forEach((a) => {
    try {
      const u = new URL(a.href);
      if (u.host === location.host || /(^|\.)longevx\.cl$/.test(u.host) || /shopify\.com$/.test(u.host)) return;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    } catch (e) {}
  });
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => lxExternal());
else lxExternal();
document.addEventListener('shopify:section:load', (e) => lxExternal(e.target));
